import React from "react";
import { Button } from "@/ui/components/ui/button.tsx";
import { Badge } from "@/ui/components/ui/badge.tsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/ui/components/ui/card.tsx";
import { Eye, Download, Trash2 } from "lucide-react";
import TemplateTable, { ColumnDefCustom } from "@/shared/components/ui/table/table.tsx";
import { IPolygonResponse } from "@/polygon/api/requests/polygon.requests.ts";
import { formatDate } from "@/polygon/utils/date-utils.ts";
import { useTranslation } from "react-i18next";

interface PolygonTableProps {
  polygons: IPolygonResponse[] | undefined;
  isLoading: boolean;
  onDelete: (id: number, name: string) => void;
  isDeletePending: boolean;
}

const PolygonTable: React.FC<PolygonTableProps> = ({
  polygons,
  isLoading,
  onDelete,
  isDeletePending,
}) => {
  const { t } = useTranslation("polygon");

  const polygonColumns: ColumnDefCustom<IPolygonResponse>[] = [
    {
      header: t("table.name"),
      accessorKey: "name",
      cell: ({ row }) => (
        <span className="font-medium">{row.original?.name || t("notAvailable")}</span>
      ),
    },
    {
      header: t("table.epsgCode"),
      accessorKey: "epsg_code",
      cell: ({ row }) => (
        <Badge variant="secondary">EPSG:{row.original?.epsg_code || t("notAvailable")}</Badge>
      ),
    },
    {
      header: t("table.checksum"),
      accessorKey: "checksum",
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original?.checksum ? `${row.original.checksum.substring(0, 8)}...` : t("notAvailable")}
        </span>
      ),
    },
    {
      header: t("table.created"),
      accessorKey: "created_at",
      cell: ({ row }) => (
        <span>{row.original?.created_at ? formatDate(row.original.created_at) : t("notAvailable")}</span>
      ),
    },
    {
      header: t("table.updated"),
      accessorKey: "updated_at",
      cell: ({ row }) => (
        <span>{row.original?.updated_at ? formatDate(row.original.updated_at) : t("notAvailable")}</span>
      ),
    },
    {
      header: t("table.actions"),
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm">
            <Eye className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (row.original?.file_url) {
                window.open(row.original.file_url, '_blank');
              }
            }}
            disabled={!row.original?.file_url}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (row.original?.id && row.original?.name) {
                onDelete(row.original.id, row.original.name);
              }
            }}
            disabled={isDeletePending || !row.original?.id}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("table.title")}</CardTitle>
        <CardDescription>
          {t("table.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TemplateTable
          items={polygons}
          columns={polygonColumns}
          filterKey="polygons"
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
};

export default PolygonTable;
